<template>
  <div class="flex flex-col w-full h-full min-h-0">
    <!-- export shortcut — same reports sheet, for the drawer's period -->
    <div class="flex items-center pb-2 flex-shrink-0">
      <button
        type="button"
        class="flex items-center gap-1.5 text-[12px] font-semibold text-primary bg-primary-50 rounded-full px-3.5 py-1.5"
        @click="openExports"
      >
        <i class="pi pi-download text-xs"/>
        ייצוא
      </button>
      <span class="text-xs text-gray-400 mr-auto">{{ periodLabel }}</span>
    </div>

    <!-- scrollable list area — fills remaining drawer height so long lists scroll -->
    <div class="flex-1 min-h-0 overflow-y-auto">
    <div v-if="isLoading" class="flex flex-col gap-2 py-2">
      <Skeleton v-for="n of 8" :key="n" height="3.2rem" borderRadius="0.75rem"/>
    </div>
    <retry-loader v-else-if="isErrorState" :isErrorState="isErrorState" @retry-function="fetchData"/>
    <div v-else-if="transactions.length === 0" class="flex flex-col items-center justify-center py-14 text-gray-400 gap-2">
      <i class="pi pi-inbox text-3xl"/>
      <span class="text-sm">אין עסקאות בתקופה שנבחרה</span>
    </div>

    <div v-else class="flex flex-col">
      <div v-if="truncated" class="flex items-center gap-2 px-1 py-2 text-xs text-orange-600">
        <i class="pi pi-exclamation-triangle"/>
        <span>מוצגות 500 העסקאות האחרונות — לרשימה מלאה השתמשו בייצוא</span>
      </div>
      <div
        v-for="(t, i) of transactions"
        :key="t._id"
        v-wave
        class="flex items-center gap-3 py-3 cursor-pointer"
        :class="{'border-b border-gray-100': i < transactions.length - 1}"
        @click="openRow(t)"
      >
        <div class="w-9 h-9 rounded-full bg-gray-100 text-gray-600 shrink-0 flex items-center justify-center text-[12px] font-bold">
          {{ (t.customerName || 'לקוח').slice(0, 2) }}
        </div>
        <div class="flex-1 min-w-0">
          <div class="text-[13.5px] font-semibold truncate">{{ t.customerName }}</div>
          <div class="text-[11.5px] text-gray-500 truncate mt-0.5">{{ t.description }}</div>
        </div>
        <div class="text-left shrink-0">
          <div class="text-[13.5px] font-bold">{{ formatCurrency(t.paidPrice ?? t.price) }}</div>
          <div class="text-[10.5px] text-gray-400 mt-0.5" dir="ltr">
            {{ formatWhen(t) }} · {{ methodLabel(t.paymentMethod) }}<template v-if="t.refundedAmount"> · הוחזר</template>
          </div>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {onMounted, ref} from 'vue';
import {DateTime} from 'luxon';
import Skeleton from 'primevue/skeleton';
import {
  IPaymentTransactionRow,
  IPaymentsResponse,
  PaymentMethod,
  PaymentRequestStatus,
} from '@torim-app/types';
import {useDrawer} from '@torim-app/shared-components';

import ExportReportsSheet from '@app/components/admin/pages/payments/ExportReportsSheet.vue';
import PaymentDetailsDrawer from '@app/components/admin/pages/customer/PaymentDetailsDrawer.vue';
import {PaymentsApi} from '@app/api/admin';
import {formatCurrency} from '@app/components/admin/analytics/analytics-charts';
import {trackEvent} from '@app/composables/posthog';

const props = defineProps<{
  startDate: string;
  endDate: string;
  periodLabel: string;
  /** notifies the page behind the drawer so its totals refresh */
  onRefunded?: () => void;
}>();

const {openDrawer} = useDrawer();

const transactions = ref<IPaymentTransactionRow[]>([]);
const isLoading = ref(true);
const isErrorState = ref(false);
const truncated = ref(false);

// the design's list shows actual charges — payments that were paid (incl. refunded)
const PAID_STATUSES = [
  PaymentRequestStatus.Success,
  PaymentRequestStatus.Refunded,
  PaymentRequestStatus.PartiallyRefunded,
];

const fetchData = async () => {
  try {
    isErrorState.value = false;
    isLoading.value = true;
    const data = await PaymentsApi.getPaymentsPageData({
      startDate: props.startDate,
      endDate: props.endDate,
      statuses: PAID_STATUSES,
    });
    transactions.value = data.transactions;
    truncated.value = !!data.truncated;
  } catch (e) {
    console.error(e);
    isErrorState.value = true;
  } finally {
    isLoading.value = false;
  }
};

const methodLabel = (m: PaymentMethod): string => {
  const map: Record<string, string> = {
    cash: 'מזומן',
    credit: 'אשראי',
    'bank-transfer': 'העברה בנקאית',
    bit: 'ביט',
    'pay-box': 'Paybox',
    'apple-pay': 'Apple Pay',
    'google-pay': 'Google Pay',
    'loyalty-program-discount': 'הטבת מועדון',
    'loyalty-program-points': 'נקודות מועדון',
  };
  return map[m] ?? m;
};

const formatWhen = (t: IPaymentTransactionRow): string => {
  const dt = DateTime.fromISO(t.date).setZone('Asia/Jerusalem');
  if (!dt.isValid) return '';
  return `${dt.toFormat('d.M')} · ${t.hour ?? dt.toFormat('HH:mm')}`;
};

const openExports = () => {
  openDrawer(ExportReportsSheet, {
    startDate: props.startDate,
    endDate: props.endDate,
    periodLabel: props.periodLabel,
  }, {title: 'ייצוא דוחות'});
};

const openRow = (row: IPaymentTransactionRow) => {
  trackEvent('payments_transaction_row_opened', {status: row.status});
  const selectedPayment = {
    _id: row._id,
    uuid: row.uuid,
    paymentReason: row.paymentReason,
    chargedDate: row.chargedDate,
    paidPrice: row.paidPrice,
    status: row.status,
    ezCountInvoiceLink: row.ezCountInvoiceLink,
    paymentMethod: row.paymentMethod,
    transactionNumber: row.transactionNumber,
    price: row.price,
    createdAt: row.createdAt,
    refundedAmount: row.refundedAmount,
    refunds: row.refunds,
  } as unknown as IPaymentsResponse;

  openDrawer(PaymentDetailsDrawer, {
    selectedPayment,
    onRefunded: (updated: IPaymentsResponse) => {
      const idx = transactions.value.findIndex(t => t._id === updated._id);
      if (idx !== -1) {
        transactions.value[idx] = {
          ...transactions.value[idx],
          status: updated.status,
          refundedAmount: updated.refundedAmount,
          refunds: updated.refunds,
        };
      }
      props.onRefunded?.();
    },
  }, {title: 'פרטי תשלום'});
};

onMounted(() => {
  trackEvent('payments_all_transactions_viewed');
  fetchData();
});
</script>
